import { DataTableColumn, DataTableProps } from './types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Checkbox,
  CheckboxSizes,
  Spinner,
  TableContainer
} from 'stubben-edge-unified-frontend-react-components';
import { TablePagination, TableSortCell } from '../Table';
import clsx from 'clsx';
import EmptyState from '@ui/EmptyState';
import IconWarning from '@icons/IconWarning';
import { Fragment, useLayoutEffect, useMemo, useRef } from 'react';
import IconError from '@icons/IconError';
import IconButton from '@ui/IconButton';
import IconClose from '@icons/IconClose';
import IconEdit from '@icons/IconEdit';

function columnHeaderAttrs(headerTestId?: string) {
  if (!headerTestId) return {};
  return {
    id: headerTestId,
    name: headerTestId,
    'data-testid': headerTestId
  } as const;
}

function cellAlignClassName(align?: DataTableColumn<unknown>['align']) {
  switch (align) {
    case 'center':
      return 'text-center';
    case 'right':
      return 'text-right';
    case 'justify':
      return 'text-justify';
    case 'left':
      return 'text-left';
    default:
      return undefined;
  }
}

function cellPropsClassName(cellProps?: {
  noWrap?: boolean;
  smallText?: boolean;
}) {
  return clsx(
    cellProps?.noWrap && 'overflow-hidden text-ellipsis whitespace-nowrap',
    cellProps?.smallText && 'text-xs'
  );
}

/**
 *  UI Component
 */
export default function DataTable<T>(props: DataTableProps<T>) {
  // Apply Default props values
  props = {
    ...{
      keyField: 'id'
    },
    ...props
  };
  const {
    showBulkError = false,
    bulkErrorText = 'Error occurred while performing the action.',
    handleBulkErrorClick,
    bulkErrorDismissTestId = 'datatable-bulk-error-dismiss',
    itemsToArchiveCount = 0,
    columns,
    data,
    keyField = 'id',
    uniqueKeyField = '',
    onRowClicked,
    onCellClick,
    count,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    paginationProps,
    actions,
    selected,
    onSelect,
    loading,
    refreshing,
    sort,
    onSortChange,
    className,
    emptyState,
    disableSelectFor,
    hasActiveFilters,
    noResultsText,
    appendAfterRow,
    isResponsive = true,
    showPagination = true, // Default value is true
    isFixedLayout = false, // Default value is false
    selectAllHeaderTestId,
    rowSelectTestIdentifier,
    actionsHeaderTestId,
    ...rest
  } = props || {};

  const numOfColumns = columns.length + (onSelect ? 1 : 0) + (actions ? 1 : 0);

  // / Selection logic
  const handleSelect = (row: T) => {
    const newSelected = selected ? [...selected] : [];
    if (
      !newSelected.find(
        (r) => r[keyField as keyof T] === row[keyField as keyof T]
      )
    ) {
      newSelected.push(row);
    }
    if (onSelect) onSelect(newSelected);
  };

  const handleDeselect = (row: T) => {
    const newSelected = selected ? [...selected] : [];
    const index = newSelected.findIndex((r) => {
      return r[keyField as keyof T] === row[keyField as keyof T];
    });
    if (index !== -1) {
      newSelected.splice(index, 1);
    }
    if (onSelect) onSelect(newSelected);
  };

  const selectedIds = selected
    ? selected.map((r) => r[keyField as keyof T])
    : [];

  const mClass = 'DataTable';
  const cls = [mClass];
  if (loading) {
    cls.push(`${mClass}--loading`);
  }
  if (refreshing) {
    cls.push(`${mClass}--refreshing`);
  }

  // BUGS-3020: stale rows under the refresh overlay must not be reachable by
  // keyboard either. TableBody does not forward refs, so find it from the root.
  const rootRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    rootRef.current
      ?.querySelector('tbody')
      ?.toggleAttribute('inert', Boolean(refreshing));
  }, [refreshing, data]);

  const refreshStatus =
    refreshing === undefined ? null : (
      <span className="sr-only" role="status" aria-live="polite">
        {refreshing ? 'Refreshing results' : ''}
      </span>
    );
  if (onRowClicked) {
    cls.push(`${mClass}--rowClickable`);
  }

  const renderLoadingRows = useMemo(() => {
    return (
      <TableRow>
        {onSelect && (
          <TableCell className="animate-pulse">
            <div className="my-2 h-2 rounded bg-slate-400"></div>
          </TableCell>
        )}
        {columns.map((item, index) => {
          return (
            <TableCell key={index + uniqueKeyField} className="animate-pulse">
              <div className="my-2 h-2 rounded bg-slate-400"></div>
            </TableCell>
          );
        })}
      </TableRow>
    );
  }, [columns, onSelect, uniqueKeyField]);

  if (emptyState && !loading && data.length === 0) {
    return (
      <div
        className={clsx(cls, className)}
        aria-busy={refreshing || undefined}
        {...rest}
      >
        {refreshStatus}
        <EmptyState {...emptyState} />
      </div>
    );
  }
  if (!data || (!loading && data.length === 0)) {
    return (
      <div
        className={clsx(cls, className)}
        aria-busy={refreshing || undefined}
        {...rest}
      >
        {refreshStatus}
        <div className={`${mClass}__NoResults`}>
          <span className={`${mClass}__NoResultsIcon`}>
            <IconWarning />
          </span>
          <span>
            {hasActiveFilters && data.length === 0
              ? noResultsText || 'No results found'
              : noResultsText || 'No results found for your filters'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={clsx(cls, className)}
      aria-busy={refreshing || undefined}
      {...rest}
    >
      {refreshStatus}
      <div className={`${mClass}__Frame`}>
        <TableContainer
          className={clsx(
            mClass,
            `${mClass}__Scroll`,
            isResponsive && `${mClass}__Scroll--responsive`
          )}
        >
          <Table
            className={clsx(isFixedLayout && 'table-fixed')}
            style={isFixedLayout ? { tableLayout: 'fixed' } : undefined}
          >
            <TableHeader>
              <TableRow>
                {onSelect && (
                  <TableHead
                    className="w-12 px-3"
                    {...columnHeaderAttrs(selectAllHeaderTestId)}
                  />
                )}
                {columns.map((column, index) =>
                  column.sortable ? (
                    <TableSortCell
                      align={column.align}
                      className={column.className}
                      key={index + uniqueKeyField}
                      {...columnHeaderAttrs(column.headerTestId)}
                      active={!!sort?.field && sort.field === column.sortField}
                      direction={sort?.direction}
                      onSortChange={(active, direction) => {
                        if (onSortChange)
                          onSortChange(
                            active && column.sortField
                              ? {
                                  field: column.sortField,
                                  direction: direction || 'asc'
                                }
                              : null
                          );
                      }}
                    >
                      {column?.nameSelector
                        ? column.nameSelector(column)
                        : column.name}
                    </TableSortCell>
                  ) : (
                    <TableHead
                      className={clsx(
                        cellAlignClassName(column.align),
                        column.className
                      )}
                      key={index + uniqueKeyField}
                      {...columnHeaderAttrs(column.headerTestId)}
                    >
                      {column?.nameSelector
                        ? column.nameSelector(column)
                        : column.name}
                    </TableHead>
                  )
                )}
                {actions && (
                  <TableHead
                    {...columnHeaderAttrs(actionsHeaderTestId)}
                  ></TableHead>
                )}
              </TableRow>
            </TableHeader>
            {showBulkError && (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length + (onSelect ? 1 : 0) + (actions ? 1 : 0)
                  }
                >
                  <div className="flex justify-end gap-4 ">
                    <div className="flex items-center gap-4 rounded bg-gray-50 p-1">
                      <IconButton size="small" color="primary">
                        <IconError />
                      </IconButton>
                      <p
                        style={{ zIndex: 100 }}
                        className="font-bold text-red-600"
                      >
                        {bulkErrorText}
                        {itemsToArchiveCount - 1 !== 0 &&
                          `( and ${itemsToArchiveCount - 1 + ' '}  more
                    errors ) `}
                      </p>
                      <IconButton
                        size="small"
                        color="default"
                        onClick={handleBulkErrorClick}
                        testIdentifier={bulkErrorDismissTestId}
                      >
                        <IconClose />
                      </IconButton>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            )}
            <TableBody>
              {loading && renderLoadingRows}
              {data?.map((row) => {
                const isRowDisabled = columns.some((column) => {
                  if (typeof column.disabled === 'function') {
                    return column.disabled(row);
                  }
                  return column.disabled === true;
                });
                const isRowDimmed = columns.some((column) => {
                  if (typeof column.dimmed === 'function') {
                    return column.dimmed(row);
                  }
                  return column.dimmed === true;
                });
                const rowClassName = isRowDisabled
                  ? `${mClass}__Row--disabled`
                  : isRowDimmed
                  ? `${mClass}__Row--dimmed`
                  : '';
                return (
                  <Fragment
                    key={`${row[keyField as keyof T]}` + uniqueKeyField}
                  >
                    <TableRow className={rowClassName}>
                      {onSelect && (
                        <TableCell className="w-12 px-3">
                          {(!disableSelectFor ||
                            disableSelectFor.filter(
                              (d) => `${d}` === `${row[keyField as keyof T]}`
                            ).length === 0) && (
                            <Checkbox
                              simple
                              size={CheckboxSizes.SM}
                              onChange={(state) =>
                                state ? handleSelect(row) : handleDeselect(row)
                              }
                              checked={
                                selectedIds.indexOf(
                                  row[keyField as keyof T]
                                ) !== -1
                              }
                              testIdentifier={
                                rowSelectTestIdentifier?.(row) ??
                                `datatable-row-select-${String(
                                  row[keyField as keyof T]
                                )}`
                              }
                            />
                          )}
                        </TableCell>
                      )}
                      {columns.map((column, index) => {
                        const shouldHide =
                          typeof column?.hideTableCell === 'function'
                            ? column.hideTableCell(row)
                            : false;
                        const isEditable =
                          typeof column?.editable === 'function'
                            ? column.editable(row)
                            : column?.editable === true;

                        if (shouldHide) return null;

                        const columnKeyBase =
                          column.sortField ??
                          (typeof column.name === 'string'
                            ? column.name
                            : index);

                        return (
                          <TableCell
                            key={`${columnKeyBase}${uniqueKeyField}`}
                            className={clsx(
                              cellAlignClassName(column.align),
                              cellPropsClassName(column.cellProps),
                              column.className,
                              isEditable && 'DataTable__Cell--editable'
                            )}
                            onClick={(e) => {
                              if (!isRowDisabled && !refreshing) {
                                if (onCellClick) {
                                  onCellClick(row, column, e);
                                } else if (onRowClicked) {
                                  onRowClicked(row, e);
                                }
                              }
                            }}
                          >
                            {isEditable ? (
                              <>
                                <span className="DataTable__EditableCellContent">
                                  {column.selector(row)}
                                </span>
                                <span
                                  className="DataTable__EditableCellIcon"
                                  aria-hidden
                                >
                                  <IconEdit size={14} />
                                </span>
                              </>
                            ) : (
                              column.selector(row)
                            )}
                          </TableCell>
                        );
                      })}

                      {actions && (
                        <TableCell className="text-right">
                          <span className={`${mClass}__Actions`}>
                            {actions(row)}
                          </span>
                        </TableCell>
                      )}
                    </TableRow>
                    {(() => {
                      const content = appendAfterRow?.(row);
                      return (
                        content && (
                          <TableRow>
                            <TableCell colSpan={numOfColumns}>
                              {content}
                            </TableCell>
                          </TableRow>
                        )
                      );
                    })()}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {refreshing && (
          <span className={`${mClass}__RefreshSpinner`} aria-hidden="true">
            <Spinner width={40} height={40} />
          </span>
        )}
      </div>
      {showPagination &&
        count !== undefined &&
        page !== undefined &&
        rowsPerPage !== undefined && (
          <TablePagination
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            {...paginationProps}
          />
        )}
    </div>
  );
}
